import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  useToast,
  HStack,
  VStack,
  Box,
} from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import { useDeleteList } from '@/api';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

interface DeleteListModalProps {
  isOpen: boolean;
  onClose: () => void;
  listName: string;
  urlKey: string;
  onSuccess?: () => void;
}

export function DeleteListModal({
  isOpen,
  onClose,
  listName,
  urlKey,
  onSuccess,
}: DeleteListModalProps) {
  const toast = useToast();
  const { mutateAsync: deleteList, isPending: isDeleting } = useDeleteList();

  const handleDelete = async () => {
    try {
      await deleteList(urlKey);

      analytics.capture(AnalyticsEvents.LIST_DELETED, {
        list_url_key: urlKey,
        list_name: listName,
      });

      toast({
        title: "List deleted",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to delete list:', error);
      toast({
        title: "Failed to delete list",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="red.100" borderRadius="var(--r-inner)" p={2}>
              <Trash2 size={20} color="var(--chakra-colors-red-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Delete List
              </Text>
              <Text fontSize="xs" color="var(--card-text-3)" fontWeight="normal">
                This action cannot be undone
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text color="var(--card-text-2)">Are you sure you want to delete "{listName}"?</Text>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="var(--card-divider)" pt={4}>
          <HStack spacing={3} w="100%">
            <Button variant="ghost" flex={1} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="red.500"
              color="white"
              _hover={{ bg: 'red.600' }}
              onClick={handleDelete}
              isLoading={isDeleting}
              leftIcon={<Trash2 size={16} />}
              flex={1}
            >
              Delete
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}